/* istanbul ignore file */
/* tslint:disable */
/* eslint-disable */
import type { loopback_Count } from '../models/loopback_Count';
import type { NewNotification } from '../models/NewNotification';
import type { Notification } from '../models/Notification';
import type { NotificationPartial } from '../models/NotificationPartial';
import type { NotificationPartialExcluding_id_adminId_coachId_userId_created_title_body_notificationType_priority_notificationObject_resourceId_image_ } from '../models/NotificationPartialExcluding_id_adminId_coachId_userId_created_title_body_notificationType_priority_notificationObject_resourceId_image_';
import type { NotificationWithRelations } from '../models/NotificationWithRelations';

import type { CancelablePromise } from '../core/CancelablePromise';
import { OpenAPI } from '../core/OpenAPI';
import { request as __request } from '../core/request';

export class NotificationControllerService {

    /**
     * @returns void
     * @throws ApiError
     */
    public static notificationControllerUpdateAllNotificationsStatusByUserId({
        id,
        userType,
    }: {
        id: string,
        userType?: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/all-notifications-status/{id}',
            path: {
                'id': id,
            },
            query: {
                'userType': userType,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static notificationControllerUpdateAllNotificationsStatus({
        requestBody,
    }: {
        requestBody?: {
            notifications?: NotificationPartialExcluding_id_adminId_coachId_userId_created_title_body_notificationType_priority_notificationObject_resourceId_image_;
            ids?: Array<string>;
        },
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/all-notifications-status',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Batch email via SendGrid (template or raw HTML); each send logged to EmailLog
     * @throws ApiError
     */
    public static notificationControllerSendBatchEmail({
        requestBody,
    }: {
        requestBody?: {
            userIds: Array<string>;
            securityKey: string;
            /**
             * Email template slug (with message)
             */
            slug?: string;
            /**
             * Template merge fields; per-user fields are merged by the service
             */
            message?: any;
            subject?: string;
            /**
             * Raw HTML body (with subject)
             */
            html?: string;
            from?: string;
            cc?: (string | Array<string>);
            receiverType?: 'coach' | 'user' | 'customer_success';
        },
    }): CancelablePromise<{
        status?: boolean;
        message?: string;
        sent?: number;
        skippedNoEmail?: number;
        failed?: number;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/batch-email',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Send BLA retake email, SMS, and push to users with active subscriptions on the BLA program
     * @throws ApiError
     */
    public static notificationControllerSendBlaRetakeReminders({
        requestBody,
    }: {
        requestBody?: {
            userIds: Array<string>;
            securityKey: string;
        },
    }): CancelablePromise<{
        status?: boolean;
        message?: string;
        eligibleCount?: number;
        emailTargetCount?: number;
        smsTargetCount?: number;
        pushTargetCount?: number;
        skippedNoContact?: number;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/bla-retake',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Send cancer risk assessment push notifications to users based on CSV file in public folder
     * @throws ApiError
     */
    public static notificationControllerSendCancerRiskAssessmentNotifications({
        requestBody,
    }: {
        requestBody?: {
            securityKey: string;
        },
    }): CancelablePromise<{
        status?: boolean;
        message?: string;
        totalProcessed?: number;
        sent?: number;
        skipped?: number;
        skippedUserIds?: Array<string>;
        failed?: number;
        failedUserIds?: Array<string>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/cancer-risk-assessment',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns loopback_Count Notification model count
     * @throws ApiError
     */
    public static notificationControllerCount({
        where,
    }: {
        where?: any,
    }): CancelablePromise<loopback_Count> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/notifications/count',
            query: {
                'where': where,
            },
        });
    }

    /**
     * @returns any Notification for Kem
     * @throws ApiError
     */
    public static notificationControllerSendKemNotification({
        requestBody,
    }: {
        requestBody?: {
            userId: string;
            title: string;
            message: string;
            securityKey: string;
            notificationType?: string;
            channel?: string;
            customize?: boolean;
            tracked?: boolean;
        },
    }): CancelablePromise<{
        success?: boolean;
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/kem',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Notification for Kem Bulk
     * @throws ApiError
     */
    public static notificationControllerSendKemBulkNotification({
        requestBody,
    }: {
        requestBody?: {
            ids: Array<string>;
            title: string;
            message: string;
            securityKey: string;
            notificationType?: string;
            channel?: string;
            customize?: boolean;
        },
    }): CancelablePromise<{
        success?: boolean;
        message?: string;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/kem-bulk',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Send post-cancer risk assessment push notifications to users based on CSV file in public folder
     * @throws ApiError
     */
    public static notificationControllerSendPostCancerRiskAssessmentNotifications({
        requestBody,
    }: {
        requestBody?: {
            securityKey: string;
        },
    }): CancelablePromise<{
        status?: boolean;
        message?: string;
        totalProcessed?: number;
        sent?: number;
        skipped?: number;
        skippedUserIds?: Array<string>;
        failed?: number;
        failedUserIds?: Array<string>;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/post-cancer-risk-assessment',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns any Notification statistics by notification type
     * @throws ApiError
     */
    public static notificationControllerStatistics({
        requestBody,
    }: {
        requestBody?: {
            notificationType: 'CHAT' | 'KEM' | 'KEM_COACH' | 'KEM_FOLLOW_UP' | 'KEM_SUBSCRIPTION' | 'REMINDER' | 'HEALTH_DIARY' | 'NEW_SUBSCRIPTION' | 'GENERAL' | 'BIRTHDAY' | 'WEIGHT_SCALE' | 'NEW_BLOG_POST' | 'COACH_APPOINTMENT' | 'SUBSCRIPTION_RENEWAL' | 'NEW_OUTLIER' | 'NEW_FORUM' | 'ACCEPTED_CONSULT' | 'NEW_CONSULT' | 'CORSANO_REMINDER' | 'BLA_RETAKE' | 'COACH_REASSIGNMENT' | 'PRE_CANCER_RISK_ASSESSMENT' | 'POST_CANCER_RISK_ASSESSMENT' | 'KEM_CAMPAIGN_COHORT_1A' | 'KEM_CAMPAIGN_COHORT_1B_1' | 'KEM_CAMPAIGN_COHORT_1B_2' | 'KEM_CAMPAIGN_COHORT_1B_3' | 'KEM_CAMPAIGN_COHORT_2_1' | 'KEM_CAMPAIGN_COHORT_2_2' | 'KEM_CAMPAIGN_COHORT_2_3' | 'KEM_CAMPAIGN_COHORT_3_1' | 'KEM_CAMPAIGN_COHORT_3_2' | 'KEM_CAMPAIGN_COHORT_3_3' | 'KEM_CAMPAIGN_COHORT_4A_1' | 'KEM_CAMPAIGN_COHORT_4A_2' | 'KEM_CAMPAIGN_COHORT_4A_3' | 'KEM_CAMPAIGN_COHORT_4B_1' | 'KEM_CAMPAIGN_COHORT_4B_2' | 'KEM_CAMPAIGN_COHORT_4B_3' | 'KEM_CAMPAIGN_COHORT_4C_1' | 'KEM_CAMPAIGN_COHORT_4C_2' | 'KEM_CAMPAIGN_COHORT_4C_3' | 'KEM_CAMPAIGN_COHORT_5_1' | 'KEM_CAMPAIGN_COHORT_5_2' | 'KEM_CAMPAIGN_COHORT_5_3' | 'KEM_CAMPAIGN_COHORT_6_1' | 'KEM_CAMPAIGN_COHORT_6_2' | 'KEM_CAMPAIGN_COHORT_6_3';
            passcode: string;
            startDate?: string;
            endDate?: string;
        },
    }): CancelablePromise<{
        notificationType?: string;
        totalCount?: number;
        seenCount?: number;
        unseenCount?: number;
        deliveredCount?: number;
        notDeliveredCount?: number;
        clickedCount?: number;
        notClickedCount?: number;
    }> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications/statistics',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns NotificationWithRelations Array of Notification model instances
     * @throws ApiError
     */
    public static notificationControllerGetAllNotificationByUserId({
        userId,
    }: {
        userId: string,
    }): CancelablePromise<Array<NotificationWithRelations>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/notifications/userId/{userId}',
            path: {
                'userId': userId,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static notificationControllerReplaceById({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: Notification,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PUT',
            url: '/notifications/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static notificationControllerUpdateById({
        id,
        requestBody,
    }: {
        id: string,
        requestBody?: NotificationPartial,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/notifications/{id}',
            path: {
                'id': id,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns NotificationWithRelations Notification model instance
     * @throws ApiError
     */
    public static notificationControllerFindById({
        id,
        filter,
    }: {
        id: string,
        filter?: any,
    }): CancelablePromise<NotificationWithRelations> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/notifications/{id}',
            path: {
                'id': id,
            },
            query: {
                'filter': filter,
            },
        });
    }

    /**
     * @returns void
     * @throws ApiError
     */
    public static notificationControllerDeleteById({
        id,
    }: {
        id: string,
    }): CancelablePromise<void> {
        return __request(OpenAPI, {
            method: 'DELETE',
            url: '/notifications/{id}',
            path: {
                'id': id,
            },
        });
    }

    /**
     * @returns Notification Notification model instance
     * @throws ApiError
     */
    public static notificationControllerCreate({
        requestBody,
    }: {
        requestBody?: NewNotification,
    }): CancelablePromise<Notification> {
        return __request(OpenAPI, {
            method: 'POST',
            url: '/notifications',
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns loopback_Count Notification PATCH success count
     * @throws ApiError
     */
    public static notificationControllerUpdateAll({
        where,
        requestBody,
    }: {
        where?: any,
        requestBody?: NotificationPartial,
    }): CancelablePromise<loopback_Count> {
        return __request(OpenAPI, {
            method: 'PATCH',
            url: '/notifications',
            query: {
                'where': where,
            },
            body: requestBody,
            mediaType: 'application/json',
        });
    }

    /**
     * @returns NotificationWithRelations Array of Notification model instances
     * @throws ApiError
     */
    public static notificationControllerFind({
        filter,
        page,
    }: {
        filter?: any,
        page?: number,
    }): CancelablePromise<Array<NotificationWithRelations>> {
        return __request(OpenAPI, {
            method: 'GET',
            url: '/notifications',
            query: {
                'filter': filter,
                'page': page,
            },
        });
    }

}
